import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  Modal, 
  Button, 
  Space, 
  Typography, 
  Row, 
  Col, 
  Avatar, 
  Tag, 
  Card,
  Divider,
  Spin,
  message,
  Select
} from 'antd';
import { 
  CalendarOutlined, 
  UserOutlined,
  ClockCircleOutlined,
  BookOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import moment from 'moment';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const { Text, Title } = Typography;

const ScheduleCalendarModal = ({ 
  isVisible, 
  onClose, 
  classGroupData = null,
  selectedStudent = null 
}) => {
  const { t } = useTranslation();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scheduleData, setScheduleData] = useState([]);
  const [classChangeLogs, setClassChangeLogs] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  
  // Student selection states
  const [showStudentSelection, setShowStudentSelection] = useState(false);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [internalSelectedStudent, setInternalSelectedStudent] = useState(selectedStudent);
  
  const calendarRef = useRef(null);
  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch students function
  const fetchStudents = useCallback(async (search = '') => {
    try {
      setLoadingStudents(true);
      const token = localStorage.getItem('token');

      const response = await axios.get(`${API_URL}/students/schedule-info-add`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: { search }
      });

      if (response.data.success) {
        const studentsList = response.data.students || [];
        setStudents(studentsList);
      } else {
        console.warn('[fetchStudents] Response returned success: false');
      }
    } catch (error) {
      console.error('[fetchStudents] Error:', error);
      message.error('Failed to fetch students');
    } finally {
      setLoadingStudents(false);
    }
  }, [API_URL]);

  // Load students on mount when student selection is shown
  useEffect(() => {
    if (showStudentSelection) {
      fetchStudents();
    }
  }, [showStudentSelection, fetchStudents]);

  // Handle student search
  const handleStudentSearch = (value) => {
    setSearchTerm(value);
    fetchStudents(value);
  };

  // Fetch class groups data (which includes classes) and class change logs
  const fetchClassGroupsData = useCallback(async () => {
    if (!isVisible || !classGroupData?.groupid) return;
    
    console.log('Fetching class groups data...', { classGroupData, isVisible });
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Use the same endpoint as your FilterTaskTable component
      const response = await axios.get(`${API_URL}/class-groups`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log('Class groups response:', response.data);

      // Find the specific class group and get its classes
      const classGroup = response.data.find(group => group.groupid === classGroupData?.groupid);
      console.log('Found class group:', classGroup);
      
      if (classGroup && classGroup.classes) {
        setScheduleData(classGroup.classes);
        console.log('Set schedule data:', classGroup.classes);
      }

      // Fetch class change logs
      const logsResponse = await axios.get(`${API_URL}/class-change/class-change-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Class change logs response:', logsResponse.data);
      setClassChangeLogs(logsResponse.data || []);
    } catch (error) {
      console.error('Error fetching class groups data:', error);
      message.error('Failed to fetch schedule data');
    } finally {
      setLoading(false);
    }
  }, [API_URL, classGroupData, isVisible]);

  useEffect(() => {
    fetchClassGroupsData();
  }, [fetchClassGroupsData]);

  // Reset selected event when modal closes
  useEffect(() => {
    if (!isVisible) {
      setSelectedEvent(null);
    }
  }, [isVisible]);




  // Helper function to get platform color
  const getPlatformColor = (platform) => {
    const platformColors = {
      'online': '#52c41a',
      'onsite': '#1890ff', 
      'athome': '#fa8c16'
    };
    return platformColors[platform] || '#d9d9d9';
  };

  // Format time helper
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  // Convert schedule data to FullCalendar events - using moment.js like FilterTaskTable
  const calendarEvents = useMemo(() => {
    console.log('Creating calendar events from schedule data:', scheduleData);
    
    return scheduleData.map((item, index) => {
      const changeLog = classChangeLogs.find(ccl => ccl.classid === item.classid);

      // Use moment.js for date handling - same approach as FilterTaskTable
      const eventDate = moment(item.date);
      const dateOnly = eventDate.format('YYYY-MM-DD'); // Get date in YYYY-MM-DD format
      
      const startDateTime = `${dateOnly}T${item.schedule_time || '00:00:00'}`;
      const endDateTime = `${dateOnly}T${item.end_time || '23:59:59'}`;


      return {
        id: `${item.classid}-${index}`,
        title: `${item.subject_name || 'Class'} - ${item.nickname || 'Teacher'}`,
        start: startDateTime,
        end: endDateTime,
        textColor: '#2d3748',
        extendedProps: {
          ...item,
          changeNote: changeLog?.note || ''
        }
      };
    });
  }, [scheduleData, classChangeLogs]);

  // Handle event click
  const handleEventClick = (clickInfo) => {
    setSelectedEvent(clickInfo.event);
  };

  // Close event details
  const closeEventDetails = () => {
    setSelectedEvent(null);
  };

  // Get class summary - use internalSelectedStudent instead of selectedStudent
  const classSummary = {
    classCode: classGroupData?.class_code || 'N/A',
    totalSessions: scheduleData.length,
    subjects: [...new Set(scheduleData.map(slot => slot.subject_name))],
    teachers: [...new Set(scheduleData.map(slot => slot.nickname).filter(Boolean))],
    platform: classGroupData?.platform || 'N/A',
    isPrivate: classGroupData?.isprivate,
    totalHours: classGroupData?.total_hours || 0
  };

  // Format minutes to hours helper
  const formatMinutesToHours = (minutes) => {
    if (!minutes || minutes === 0) return '0 hrs';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}.${remainingMinutes} hrs` : `${hours} hrs`;
  };

// Updated PDF generation function - force full width calendar
const handlePrintCalendar = async () => {
  if (!calendarRef.current) return;
  
  setIsPrinting(true);
  try {
    // Hide the event details panel temporarily for cleaner print
    const originalSelectedEvent = selectedEvent;
    setSelectedEvent(null);
    
    // Wait a bit for the UI to update
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Get the FullCalendar element
    const calendarElement = calendarRef.current;
    const fullCalendarEl = calendarElement.querySelector('.fc');
    
    if (!fullCalendarEl) {
      throw new Error('Calendar element not found');
    }

    // Get the calendar container (the div with ref)
    const calendarContainer = calendarRef.current;

    // Force FullCalendar to re-render and ensure all events are visible
    const calendarApi = calendarElement.querySelector('.fc')?.closest('[data-fc]') || 
                       window.FullCalendar?.getCalendar?.(fullCalendarEl);
    
    if (calendarApi) {
      // Force a re-render to ensure all events are displayed
      calendarApi.render?.();
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Store original styles for both container and calendar
    const containerOriginalStyles = {
      width: calendarContainer.style.width,
      height: calendarContainer.style.height,
      maxWidth: calendarContainer.style.maxWidth,
      overflow: calendarContainer.style.overflow
    };

    const originalStyles = {
      overflow: fullCalendarEl.style.overflow,
      height: fullCalendarEl.style.height,
      maxHeight: fullCalendarEl.style.maxHeight,
      minHeight: fullCalendarEl.style.minHeight,
      width: fullCalendarEl.style.width
    };
    
    // Force container to a specific large width for capture
    const captureWidth = 1400; // Large fixed width
    const captureHeight = 1000; // Large fixed height
    
    calendarContainer.style.width = `${captureWidth}px`;
    calendarContainer.style.height = `${captureHeight}px`;
    calendarContainer.style.maxWidth = 'none';
    calendarContainer.style.overflow = 'visible';
    
    // Temporarily modify calendar for full capture with forced dimensions
    fullCalendarEl.style.overflow = 'visible';
    fullCalendarEl.style.height = `${captureHeight - 32}px`; // Account for padding
    fullCalendarEl.style.maxHeight = 'none';
    fullCalendarEl.style.minHeight = `${captureHeight - 32}px`;
    fullCalendarEl.style.width = `${captureWidth - 32}px`; // Account for padding
    
    // Force the table structure to use full width
    const fcTable = fullCalendarEl.querySelector('.fc-scrollgrid');
    const fcTableSync = fullCalendarEl.querySelector('.fc-scrollgrid-sync-table');
    const originalTableStyles = {};
    
    if (fcTable) {
      originalTableStyles.tableWidth = fcTable.style.width;
      fcTable.style.width = '100%';
      fcTable.style.minWidth = `${captureWidth - 64}px`;
    }
    
    if (fcTableSync) {
      originalTableStyles.tableSyncWidth = fcTableSync.style.width;
      fcTableSync.style.width = '100%';
      fcTableSync.style.minWidth = `${captureWidth - 64}px`;
    }
    
    // Force all table cells to distribute evenly
    const fcDayCells = fullCalendarEl.querySelectorAll('.fc-col-header-cell, .fc-daygrid-day');
    const originalCellStyles = [];
    fcDayCells.forEach((cell, index) => {
      originalCellStyles[index] = {
        width: cell.style.width,
        minWidth: cell.style.minWidth
      };
      cell.style.width = `${(captureWidth - 64) / 7}px`; // 7 days in a week
      cell.style.minWidth = `${(captureWidth - 64) / 7}px`;
    });
    
    // Also modify the view container
    const fcView = fullCalendarEl.querySelector('.fc-view-harness');
    const fcDayGrid = fullCalendarEl.querySelector('.fc-daygrid-body');
    const originalViewStyles = {};
    
    if (fcView) {
      originalViewStyles.overflow = fcView.style.overflow;
      originalViewStyles.height = fcView.style.height;
      originalViewStyles.width = fcView.style.width;
      fcView.style.overflow = 'visible';
      fcView.style.height = 'auto';
      fcView.style.width = '100%';
    }
    
    if (fcDayGrid) {
      originalViewStyles.gridOverflow = fcDayGrid.style.overflow;
      originalViewStyles.gridWidth = fcDayGrid.style.width;
      fcDayGrid.style.overflow = 'visible';
      fcDayGrid.style.width = '100%';
    }
    
    // Force all event elements to be visible and properly styled
    const eventElements = fullCalendarEl.querySelectorAll('.fc-event');
    const originalEventStyles = [];
    eventElements.forEach((event, index) => {
      originalEventStyles[index] = {
        position: event.style.position,
        zIndex: event.style.zIndex,
        opacity: event.style.opacity
      };
      event.style.position = 'relative';
      event.style.zIndex = '1';
      event.style.opacity = '1';
    });
    
    // Force calendar to re-render with new dimensions
    if (calendarApi) {
      calendarApi.updateSize?.();
      calendarApi.render?.();
    }
    
    // Wait for layout to update and force a repaint
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Force browser repaint
    fullCalendarEl.style.display = 'none';
    // Trigger reflow
    void fullCalendarEl.offsetHeight;
    fullCalendarEl.style.display = '';
    
    await new Promise(resolve => setTimeout(resolve, 500));

    // Capture the calendar container (which now has fixed dimensions)
    const canvas = await html2canvas(calendarContainer, {
      scale: 2, // Reduced scale since we're using larger fixed dimensions
      backgroundColor: '#ffffff',
      logging: false,
      allowTaint: true,
      useCORS: true,
      width: captureWidth,
      height: captureHeight,
      windowWidth: captureWidth,
      windowHeight: captureHeight,
      ignoreElements: (element) => {
        // Skip any problematic elements
        return element.classList?.contains('fc-popover') || 
               element.classList?.contains('ant-tooltip');
      },
      onclone: (clonedDoc, element) => {
        // Ensure cloned calendar has proper styling
        const clonedContainer = clonedDoc.querySelector('[data-calendar-container]') || 
                               clonedDoc.getElementById(calendarContainer.id) ||
                               clonedDoc.querySelector('.calendar-container');
        
        if (clonedContainer) {
          clonedContainer.style.width = `${captureWidth}px`;
          clonedContainer.style.height = `${captureHeight}px`;
          clonedContainer.style.overflow = 'visible';
        }
        
        const clonedCalendar = clonedDoc.querySelector('.fc');
        if (clonedCalendar) {
          clonedCalendar.style.overflow = 'visible';
          clonedCalendar.style.height = `${captureHeight - 32}px`;
          clonedCalendar.style.maxHeight = 'none';
          clonedCalendar.style.width = `${captureWidth - 32}px`;
          
          // Force cloned table structure to use full width
          const clonedTable = clonedCalendar.querySelector('.fc-scrollgrid');
          const clonedTableSync = clonedCalendar.querySelector('.fc-scrollgrid-sync-table');
          
          if (clonedTable) {
            clonedTable.style.width = '100%';
            clonedTable.style.minWidth = `${captureWidth - 64}px`;
          }
          
          if (clonedTableSync) {
            clonedTableSync.style.width = '100%';
            clonedTableSync.style.minWidth = `${captureWidth - 64}px`;
          }
          
          // Force cloned cells to distribute evenly
          const clonedCells = clonedCalendar.querySelectorAll('.fc-col-header-cell, .fc-daygrid-day');
          clonedCells.forEach(cell => {
            cell.style.width = `${(captureWidth - 64) / 7}px`;
            cell.style.minWidth = `${(captureWidth - 64) / 7}px`;
          });
          
          // Ensure all events are visible in the clone
          const clonedEvents = clonedCalendar.querySelectorAll('.fc-event');
          clonedEvents.forEach(event => {
            event.style.position = 'relative';
            event.style.zIndex = '1';
            event.style.opacity = '1';
            event.style.visibility = 'visible';
          });
        }
      }
    });

    // Restore all original styles
    Object.assign(calendarContainer.style, containerOriginalStyles);
    Object.assign(fullCalendarEl.style, originalStyles);
    
    // Restore table styles
    if (fcTable) {
      fcTable.style.width = originalTableStyles.tableWidth || '';
      fcTable.style.minWidth = '';
    }
    
    if (fcTableSync) {
      fcTableSync.style.width = originalTableStyles.tableSyncWidth || '';
      fcTableSync.style.minWidth = '';
    }
    
    // Restore cell styles
    fcDayCells.forEach((cell, index) => {
      if (originalCellStyles[index]) {
        cell.style.width = originalCellStyles[index].width || '';
        cell.style.minWidth = originalCellStyles[index].minWidth || '';
      }
    });
    
    if (fcView) {
      fcView.style.overflow = originalViewStyles.overflow || '';
      fcView.style.height = originalViewStyles.height || '';
      fcView.style.width = originalViewStyles.width || '';
    }
    
    if (fcDayGrid) {
      fcDayGrid.style.overflow = originalViewStyles.gridOverflow || '';
      fcDayGrid.style.width = originalViewStyles.gridWidth || '';
    }
    
    // Restore event styles
    eventElements.forEach((event, index) => {
      if (originalEventStyles[index]) {
        Object.assign(event.style, originalEventStyles[index]);
      }
    });

    // Force calendar to resize back to original
    if (calendarApi) {
      calendarApi.updateSize?.();
      calendarApi.render?.();
    }

    // Restore the selected event
    setSelectedEvent(originalSelectedEvent);

    // Create PDF with the captured full-width calendar
    const pdf = new jsPDF('l', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    
    // Compact header
    const headerHeight = 25;
    const footerHeight = 10;
    const margins = 8;
    
    // Use most of the page for the calendar
    const availableWidth = pageWidth - (margins * 2);
    const availableHeight = pageHeight - headerHeight - footerHeight - 3;
    
    // Scale the captured image to use full available width
    const imgAspectRatio = canvas.width / canvas.height;
    let finalWidth = availableWidth;
    let finalHeight = finalWidth / imgAspectRatio;
    
    // If height is too large, scale to fit height but keep as wide as possible
    if (finalHeight > availableHeight) {
      finalHeight = availableHeight;
      finalWidth = finalHeight * imgAspectRatio;
      
      // If we still have width available, use it (stretch the calendar)
      if (finalWidth < availableWidth) {
        finalWidth = availableWidth; // Use full width even if it changes aspect ratio slightly
      }
    }

    const imgX = margins;
    const imgY = headerHeight + 3;

    // Compact header - Row 1: Class code and basic info
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    const classTitle = `${classSummary.classCode} - Calendar View`;
    const classInfo = `${classSummary.totalSessions} sessions • ${formatMinutesToHours(classSummary.totalHours)} • ${classSummary.isPrivate ? 'Private' : 'Group'} • ${classSummary.platform}`;
    
    pdf.text(classTitle, margins, 12);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(classInfo, pageWidth - margins, 12, { align: 'right' });

    // Row 2: Student info (if available)
    if (internalSelectedStudent) {
      pdf.setFontSize(10);
      const studentInfo = `Student: ${internalSelectedStudent.firstname} ${internalSelectedStudent.lastname}`;
      const contactInfo = `${internalSelectedStudent.email || 'N/A'} • ${internalSelectedStudent.phone || 'N/A'}`;
      
      pdf.text(studentInfo, margins, 20);
      pdf.text(contactInfo, pageWidth - margins, 20, { align: 'right' });
    }

    // Add the full-width calendar image
    pdf.addImage(canvas.toDataURL('image/png', 1.0), 'PNG', imgX, imgY, finalWidth, finalHeight);

    // Minimal footer
    pdf.setFontSize(7);
    pdf.text(`Generated: ${moment().format('DD/MM/YY HH:mm')}`, margins, pageHeight - 5);
    pdf.text('KEAES ACADEMICS', pageWidth - margins, pageHeight - 5, { align: 'right' });

    // Save
    const fileName = internalSelectedStudent 
      ? `${classSummary.classCode}_${internalSelectedStudent.firstname}_${internalSelectedStudent.lastname}_Calendar.pdf`
      : `${classSummary.classCode}_Calendar.pdf`;
    
    pdf.save(fileName);
    message.success('Full width calendar PDF generated successfully!');
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    message.error('Failed to generate PDF: ' + error.message);
  } finally {
    setIsPrinting(false);
  }
};

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={null}
      title={null}
      width="95vw"
      style={{ maxWidth: '1400px' }}
      styles={{
        header: { display: 'none' },
        body: { padding: '24px', height: '85vh', overflow: 'hidden' }
      }}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Determine which view to show */}
        {showStudentSelection ? (
          /* Student Selection View */
          <>
            {/* Student Selection Header */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(125, 220, 192, 0.1) 0%, rgba(244, 248, 187, 0.1) 100%)',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '24px',
              border: '1px solid rgba(125, 220, 192, 0.2)'
            }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Space align="center">
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: '#7DDCC0',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <UserOutlined style={{ fontSize: '24px', color: 'white' }} />
                    </div>
                    <div>
                      <Title level={4} style={{ margin: 0, color: '#2d3748' }}>Select Student</Title>
                      <Text style={{ color: '#718096' }}>Choose a student for this calendar</Text>
                    </div>
                  </Space>
                </Col>
                <Col>
                  <Button 
                    onClick={() => setShowStudentSelection(false)}
                    style={{
                      borderColor: '#7DDCC0',
                      color: '#5A9D8A',
                      borderRadius: '8px'
                    }}
                  >
                    Back to Calendar
                  </Button>
                </Col>
              </Row>
            </div>

            <Card style={{ borderRadius: '12px', border: '1px solid rgba(125, 220, 192, 0.2)' }}>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ color: '#2d3748' }}>Search and Select Student:</Text>
              </div>
              
              <Select
                showSearch
                placeholder="Search by name, nickname, email, or phone..."
                style={{ width: '100%', marginBottom: 16 }}
                loading={loadingStudents}
                onSearch={handleStudentSearch}
                onChange={(value) => {
                  const student = students.find(s => s.value === value);
                  if (student) {
                    const mappedStudent = {
                      userid: student.value,
                      firstname: student.label.split(' ')[0] || '',
                      lastname: student.label.split(' ').slice(1).join(' ') || '',
                      nickname: '',
                      email: student.contactInfo?.email || '',
                      phone: student.contactInfo?.phone || '',
                      address: student.contactInfo?.address || '',
                      status: student.status || 'active',
                      date_of_birth: student.date_of_birth
                    };
                    setInternalSelectedStudent(mappedStudent);
                  }
                }}
                filterOption={false}
                notFoundContent={loadingStudents ? 'Loading...' : 'No students found'}
              >
                {students.map(student => (
                  <Select.Option key={student.value} value={student.value}>
                    <div>
                      <div style={{ fontWeight: 'bold' }}>
                        {student.label}
                      </div>
                    </div>
                  </Select.Option>
                ))}
              </Select>

              {internalSelectedStudent && (
                <Card 
                  size="small" 
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(125, 220, 192, 0.1) 0%, rgba(244, 248, 187, 0.1) 100%)',
                    border: '1px solid rgba(125, 220, 192, 0.3)',
                    borderRadius: '12px'
                  }}
                >
                  <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ color: '#2d3748' }}>Selected Student:</Text>
                  </div>
                  
                  <Row gutter={[24, 12]}>
                    <Col span={12}>
                      <div style={{ marginBottom: 12 }}>
                        <Text strong style={{ color: '#2d3748' }}>Name:</Text>
                        <br />
                        <Text style={{ color: '#718096' }}>
                          {internalSelectedStudent.firstname} {internalSelectedStudent.lastname}
                          {internalSelectedStudent.nickname && (
                            <span style={{ color: '#7DDCC0' }}> ({internalSelectedStudent.nickname})</span>
                          )}
                        </Text>
                      </div>
                      
                      <div style={{ marginBottom: 12 }}>
                        <Text strong style={{ color: '#2d3748' }}>Email:</Text>
                        <br />
                        <Text style={{ color: '#718096' }}>{internalSelectedStudent.email || 'N/A'}</Text>
                      </div>

                      <div style={{ marginBottom: 12 }}>
                        <Text strong style={{ color: '#2d3748' }}>Phone:</Text>
                        <br />
                        <Text style={{ color: '#718096' }}>{internalSelectedStudent.phone || 'N/A'}</Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: 12 }}>
                        <Text strong style={{ color: '#2d3748' }}>Status:</Text>
                        <br />
                        <Tag 
                          color={internalSelectedStudent.status === 'active' ? '#52c41a' : '#ff4d4f'}
                          style={{ borderRadius: '12px', padding: '2px 8px' }}
                        >
                          {internalSelectedStudent.status?.toUpperCase()}
                        </Tag>
                      </div>

                      <div style={{ marginBottom: 12 }}>
                        <Text strong style={{ color: '#2d3748' }}>Date of Birth:</Text>
                        <br />
                        <Text style={{ color: '#718096' }}>
                          {internalSelectedStudent.date_of_birth 
                            ? new Date(internalSelectedStudent.date_of_birth).toLocaleDateString('en-GB')
                            : 'N/A'
                          }
                        </Text>
                      </div>
                      
                      <div style={{ marginBottom: 12 }}>
                        <Text strong style={{ color: '#2d3748' }}>Address:</Text>
                        <br />
                        <Text style={{ color: '#718096' }}>{internalSelectedStudent.address || 'N/A'}</Text>
                      </div>
                    </Col>
                  </Row>
                  
                  <div style={{ marginTop: 20, textAlign: 'right' }}>
                    <Space>
                      <Button 
                        onClick={() => setInternalSelectedStudent(null)}
                        style={{
                          borderColor: '#ff4757',
                          color: '#ff4757',
                          borderRadius: '8px'
                        }}
                      >
                        Clear Selection
                      </Button>
                      <Button 
                        type="primary" 
                        onClick={() => setShowStudentSelection(false)}
                        style={{
                          background: '#7DDCC0',
                          border: 'none',
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(125, 220, 192, 0.3)'
                        }}
                      >
                        Confirm Selection
                      </Button>
                    </Space>
                  </div>
                </Card>
              )}
            </Card>
          </>
        ) : (
          /* Calendar View */
          <>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(125, 220, 192, 0.1) 0%, rgba(244, 248, 187, 0.1) 100%)',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '20px',
              border: '1px solid rgba(125, 220, 192, 0.2)'
            }}>
              <Row align="middle" justify="space-between">
                <Col>
                  <Space align="center">
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: '#7DDCC0',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CalendarOutlined style={{ fontSize: '24px', color: 'white' }} />
                    </div>
                    <div>
                      <Title level={3} style={{ margin: 0, color: '#2d3748' }}>
                        {classSummary.classCode} - Calendar View
                      </Title>
                      <Text style={{ color: '#718096' }}>
                        {classSummary.totalSessions} sessions • {formatMinutesToHours(classSummary.totalHours)}
                      </Text>
                    </div>
                  </Space>
                </Col>
                <Col>
                  <Space>
                    <Tag color="#7DDCC0" style={{ borderRadius: '20px', padding: '4px 12px' }}>
                      {classSummary.isPrivate ? 'Private' : 'Group'}
                    </Tag>
                    <Tag color="#5A9D8A" style={{ borderRadius: '20px', padding: '4px 12px' }}>
                      {classSummary.platform}
                    </Tag>
                  </Space>
                </Col>
              </Row>

              {internalSelectedStudent && (
                <>
                  <Divider style={{ margin: '16px 0', borderColor: 'rgba(125, 220, 192, 0.3)' }} />
                  <Row align="middle">
                    <Col>
                      <Space align="center">
                        <Avatar 
                          size={40}
                          style={{ 
                            background: '#7DDCC0',
                            border: '2px solid white'
                          }}
                          icon={<UserOutlined />}
                        >
                          {internalSelectedStudent.firstname?.[0]?.toUpperCase()}
                        </Avatar>
                        <div>
                          <Text strong style={{ color: '#2d3748' }}>
                            {internalSelectedStudent.firstname} {internalSelectedStudent.lastname}
                          </Text>
                          <br />
                          <Text style={{ color: '#718096', fontSize: '12px' }}>
                            {internalSelectedStudent.email} • {internalSelectedStudent.phone}
                          </Text>
                        </div>
                      </Space>
                    </Col>
                  </Row>
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ marginBottom: '16px' }}>
              <Space>
                <Button 
                  type="primary"
                  icon={<UserOutlined />}
                  onClick={() => setShowStudentSelection(true)}
                  style={{
                    background: '#7DDCC0',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(125, 220, 192, 0.3)'
                  }}
                >
                  {internalSelectedStudent ? 'Change Student' : 'Select Student'}
                </Button>
                
                <Button 
                  icon={<DownloadOutlined />}
                  onClick={handlePrintCalendar}
                  loading={isPrinting}
                  style={{
                    background: '#5A9D8A',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(90, 157, 138, 0.3)'
                  }}
                >
                  {isPrinting ? 'Generating PDF...' : 'Download PDF'}
                </Button>
                
              </Space>
            </div>

        {/* Calendar Container */}
        <div 
          ref={calendarRef}
          style={{ 
            flex: 1, 
            background: 'white',
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid rgba(125, 220, 192, 0.2)',
            boxShadow: '0 2px 8px rgba(125, 220, 192, 0.1)',
            overflow: 'hidden'
          }}
        >
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <Row gutter={16} style={{ height: '100%' }}>
              {/* Calendar */}
              <Col span={selectedEvent ? 16 : 24} style={{ height: '100%' }}>
                <div style={{ height: '100%' }}>
                  <FullCalendar
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    initialDate={scheduleData.length > 0 ? moment(scheduleData[0].date).format('YYYY-MM-DD') : undefined}
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: ''
                    }}
                    events={calendarEvents}
                    eventClick={handleEventClick}
                    height="100%"
                    eventTimeFormat={{
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    }}
                    slotLabelFormat={{
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: false, 
                                }}
                    eventDisplay="block"
                    eventContent={(eventInfo) => (
                      <div style={{ 
                        padding: '4px 2px', 
                        fontSize: '10px',
                        lineHeight: '1.3',
                        overflow: 'hidden',
                        backgroundColor: 'rgba(176, 255, 233, 0.27)',
                        borderRadius: '4px',
                        minHeight: '40px'
                      }}>
                        <div style={{ 
                          fontWeight: 'bold', 
                          color: '#2d3748',
                          marginBottom: '2px',
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis' 
                        }}>
                          {eventInfo.event.extendedProps.subject_name}
                        </div>
                        
                        <div style={{ 
                          fontSize: '10px',
                          color: '#4a5568',
                          marginBottom: '2px',
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis' 
                        }}>
                          👤 {eventInfo.event.extendedProps.nickname}
                        </div>
                        
                        <div style={{ 
                          fontSize: '10px',
                          color: '#718096',
                          marginBottom: '0px'
                        }}>
                          ⏰ {formatTime(eventInfo.event.extendedProps.schedule_time)} - {formatTime(eventInfo.event.extendedProps.end_time)}  ({eventInfo.event.extendedProps.schedule_hour})
                        </div>
                        
                        
                        {eventInfo.event.extendedProps.changeReason.text !== '-' && eventInfo.event.extendedProps.changeReason.text !== '' && (
                          <div style={{ 
                            fontSize: '9px', 
                            color: eventInfo.event.extendedProps.changeReason.color,
                            fontWeight: 'bold',
                            backgroundColor: eventInfo.event.extendedProps.changeReason.bgColor,
                            padding: '1px 4px',
                            borderRadius: '3px',
                            marginTop: '0px'
                          }}>
                            {eventInfo.event.extendedProps.changeReason.text}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
              </Col>

              {/* Event Details Panel */}
              {selectedEvent && (
                <Col span={8} style={{ height: '100%' }}>
                  <Card 
                    style={{ 
                      height: '100%',
                      border: '1px solid rgba(125, 220, 192, 0.3)',
                      borderRadius: '12px'
                    }}
                    bodyStyle={{ height: '100%', overflow: 'auto' }}
                  >
                    <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                      <Button 
                        size="small" 
                        onClick={closeEventDetails}
                        style={{
                          borderColor: '#ff4757',
                          color: '#ff4757',
                          borderRadius: '6px'
                        }}
                      >
                        ✕
                      </Button>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <Title level={5} style={{ color: '#2d3748', marginBottom: '8px' }}>
                        Class Details
                      </Title>
                      
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <div>
                          <Text strong><BookOutlined /> Subject:</Text>
                          <br />
                          <Text>{selectedEvent.extendedProps.subject_name}</Text>
                        </div>
                        
                        <div>
                          <Text strong><TeamOutlined /> Teacher:</Text>
                          <br />
                          <Text>{selectedEvent.extendedProps.nickname || 'N/A'}</Text>
                        </div>
                        
                        <div>
                          <Text strong><ClockCircleOutlined /> Time:</Text>
                          <br />
                          <Text>
                            {formatTime(selectedEvent.extendedProps.schedule_time)} - {formatTime(selectedEvent.extendedProps.end_time)}
                          </Text>
                        </div>
                        
                        <div>
                          <Text strong>Duration:</Text>
                          <br />
                          <Text>{selectedEvent.extendedProps.schedule_hour} hours</Text>
                        </div>
                        
                        <div>
                          <Text strong><EnvironmentOutlined /> Platform:</Text>
                          <br />
                          <Tag color={getPlatformColor(selectedEvent.extendedProps.platform)}>
                            {selectedEvent.extendedProps.platform?.toUpperCase() || 'N/A'}
                          </Tag>
                        </div>
                        
                        {selectedEvent.extendedProps.changeReason.text !== '-' && selectedEvent.extendedProps.changeReason.text !== '' && (
                          <div>
                            <Text strong>Status:</Text>
                            <br />
                            <Tag 
                              style={{ 
                                color: selectedEvent.extendedProps.changeReason.color,
                                borderColor: selectedEvent.extendedProps.changeReason.color,
                                borderRadius: '12px'
                              }}
                            >
                              {selectedEvent.extendedProps.changeReason.text}
                            </Tag>
                          </div>
                        )}
                        
                        {selectedEvent.extendedProps.changeNote && (
                          <div>
                            <Text strong>Note:</Text>
                            <br />
                            <Text style={{ 
                              fontSize: '12px', 
                              color: '#718096',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {selectedEvent.extendedProps.changeNote}
                            </Text>
                          </div>
                        )}
                      </Space>
                    </div>
                  </Card>
                </Col>
              )}
            </Row>
          )}
        </div>
          </>
        )}
      </div>
    </Modal>
  );
};
export default ScheduleCalendarModal;